import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BadgeCheck, XCircle, Plus, Receipt, ExternalLink, Undo2, FileText,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { money, date, dateTime, humanise, relative } from '@/lib/format';
import {
  Badge, Button, Card, Checkbox, ConfirmDialog, EmptyState, Field, Input, LoadingBlock,
  Modal, MoneyInput, PageHeader, Select, Table, TableWrap, Tabs, Td, Th, Avatar,
  useToast, statusTone, DetailRow,
} from '@/components/ui';
import { DriveImage } from '@/components/Files';
import type { Payment, Tenant, Charge } from '@shared/types';
import { BankImport } from '@/components/BankImport';
import { PaymentSchedules } from '@/components/PaymentSchedules';

const METHODS = ['bank_transfer', 'mobile_money', 'cash', 'card', 'cheque', 'other'] as const;

interface ExpectedRow {
  charge_id: string;
  tenant_id: string;
  tenant_name: string | null;
  property_name: string | null;
  room_name: string | null;
  description: string;
  due_date: string;
  days_overdue: number;
  cycle: string;
  expected_cents: number;
  pending_cents: number;
}

interface ExpectedResponse {
  count: number;
  total_expected_cents: number;
  confirms_automatically: boolean;
  expected: ExpectedRow[];
}

/**
 * Rent due from tenants on a standing order.
 *
 * The point of this screen is that confirming four hundred payments is one
 * decision rather than four hundred. What it deliberately does NOT do is
 * assume the money arrived: a standing order can be cancelled or bounce, and
 * marking rent paid on the strength of one being set up would mean nobody
 * notices a tenant stopped paying. So the landlord checks this against their
 * bank statement, unticks anything missing, and confirms the rest.
 */
function ExpectedTab({ onChanged }: { onChanged: () => void }) {
  const toast = useToast();
  const { can } = useAuth();
  const { data, loading, reload } = useApi<ExpectedResponse>('/payments/expected');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const rows = data?.expected ?? [];
  const currency = useAuth().staff?.org.currency ?? 'GBP';

  const chosenTotal = rows
    .filter((r) => chosen.has(r.charge_id))
    .reduce((sum, r) => sum + r.expected_cents, 0);

  function toggle(id: string) {
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function confirm() {
    setBusy(true);
    try {
      const res = await api.post<{ confirmed: number; skipped: number }>(
        '/payments/confirm-expected', { charge_ids: [...chosen] },
      );
      toast.success(
        `${res.confirmed} payment${res.confirmed === 1 ? '' : 's'} recorded`
        + (res.skipped ? `, ${res.skipped} skipped` : ''),
      );
      setChosen(new Set());
      reload();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not confirm those payments');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Card><div className="p-5"><LoadingBlock rows={6} /></div></Card>;

  if (rows.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Receipt className="h-6 w-6" />}
          title="Nothing expected right now"
          message={
            'Tenants set to pay by standing order show up here when their rent falls due. '
            + "Set a tenant's payment method on their lease."
          }
        />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-line bg-surface p-4 sm:p-5">
        <p className="text-sm text-muted leading-relaxed">
          These tenants pay by standing order, so nothing arrives for them to claim.
          Check the list against your bank statement, untick anything that has not
          landed, and confirm the rest in one go.{' '}
          <strong className="text-ink">Nothing here is counted as paid until you confirm it.</strong>
        </p>
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-line">
          <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer">
            <Checkbox
              checked={chosen.size === rows.length && rows.length > 0}
              onChange={() => setChosen(
                chosen.size === rows.length ? new Set() : new Set(rows.map((r) => r.charge_id)),
              )}
            />
            {chosen.size > 0 ? `${chosen.size} selected · ${money(chosenTotal, currency)}` : 'Select all'}
          </label>
          {can('manager') && (
            <Button
              variant="primary"
              disabled={chosen.size === 0 || busy}
              loading={busy}
              onClick={() => void confirm()}
            >
              Confirm {chosen.size > 0 ? chosen.size : ''} received
            </Button>
          )}
        </div>

        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th className="w-10" />
                <Th>Tenant</Th>
                <Th>For</Th>
                <Th align="right">Expected</Th>
                <Th>Due</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.charge_id} className="border-t border-line">
                  <Td>
                    <Checkbox
                      checked={chosen.has(r.charge_id)}
                      onChange={() => toggle(r.charge_id)}
                    />
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Avatar name={r.tenant_name ?? '?'} size="sm" />
                      <div className="min-w-0">
                        <Link to={`/app/tenants/${r.tenant_id}`} className="font-medium hover:text-brand truncate block">
                          {r.tenant_name}
                        </Link>
                        <p className="text-xs text-faint truncate">
                          {r.property_name}{r.room_name ? ` · ${r.room_name}` : ''}
                        </p>
                      </div>
                    </div>
                  </Td>
                  <Td className="text-muted max-w-[220px]">
                    <span className="truncate block">{r.description}</span>
                    {r.pending_cents > 0 && (
                      <span className="text-xs text-warn">
                        {money(r.pending_cents, currency)} already claimed — check before confirming
                      </span>
                    )}
                  </Td>
                  <Td align="right" className="font-semibold tnum whitespace-nowrap">
                    {money(r.expected_cents, currency)}
                  </Td>
                  <Td className="whitespace-nowrap">
                    <span className="text-muted">{date(r.due_date)}</span>
                    {r.days_overdue > 0 && (
                      <Badge tone="danger" className="ml-2">{r.days_overdue}d late</Badge>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </Card>
    </div>
  );
}

export default function Payments() {
  const { currency, can } = useAuth();
  const toast = useToast();

  const [tab, setTab] = useState('pending');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reviewing, setReviewing] = useState<Payment | null>(null);
  const [recording, setRecording] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const statusFilter = tab === 'all' || tab === 'expected' ? undefined : tab;
  const { data: expected } = useApi<ExpectedResponse>('/payments/expected');
  const expectedCount = expected?.count;
  const { data: payments, loading, reload } = useApi<Payment[]>('/payments', {
    status: statusFilter, limit: 300,
  });
  const { data: counts } = useApi<Payment[]>('/payments', { limit: 500 });

  const tabCounts = useMemo(() => {
    const list = counts ?? [];
    return {
      pending: list.filter((p) => p.status === 'pending').length,
      verified: list.filter((p) => p.status === 'verified').length,
      rejected: list.filter((p) => p.status === 'rejected').length,
    };
  }, [counts]);

  const rows = payments ?? [];
  const pendingIds = rows.filter((p) => p.status === 'pending').map((p) => p.id);
  const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selected.has(id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function bulkVerify() {
    setBusy(true);
    try {
      const res = await api.post<{ verified: number }>('/payments/bulk-verify', {
        ids: [...selected],
      });
      toast.success(`${res.verified} payment${res.verified === 1 ? '' : 's'} verified`);
      setSelected(new Set());
      setBulkOpen(false);
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not verify those payments');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payments"
        subtitle="Nothing counts towards a balance until it is verified here"
        action={
          <>
            {selected.size > 0 && can('manager') && (
              <Button
                variant="primary"
                icon={<BadgeCheck className="h-4 w-4" />}
                onClick={() => setBulkOpen(true)}
              >
                Verify {selected.size}
              </Button>
            )}
            <Button
              variant={selected.size > 0 ? 'outline' : 'primary'}
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setRecording(true)}
            >
              Record payment
            </Button>
          </>
        }
      />

      <Tabs
        active={tab}
        onChange={(t) => { setTab(t); setSelected(new Set()); }}
        tabs={[
          { id: 'statement', label: 'Bank statement' },
          { id: 'expected', label: 'Expected by standing order', count: expectedCount },
          { id: 'schedules', label: 'Standing orders' },
          { id: 'pending', label: 'Awaiting verification', count: tabCounts.pending },
          { id: 'verified', label: 'Verified', count: tabCounts.verified },
          { id: 'rejected', label: 'Rejected', count: tabCounts.rejected },
          { id: 'all', label: 'All' },
        ]}
      />

      {tab === 'statement' ? <BankImport />
        : tab === 'schedules' ? <PaymentSchedules />
        : tab === 'expected' ? <ExpectedTab onChanged={reload} /> : (
      <Card>
        {loading ? (
          <div className="p-5"><LoadingBlock rows={6} /></div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Receipt className="h-6 w-6" />}
            title={tab === 'pending' ? 'Nothing waiting for review' : 'No payments here'}
            message={
              tab === 'pending'
                ? 'When a tenant submits a payment from their portal, it appears here for you to confirm.'
                : undefined
            }
            action={
              tab === 'pending'
                ? <Button variant="primary" size="sm" onClick={() => setRecording(true)}>Record a payment</Button>
                : undefined
            }
          />
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  {tab === 'pending' && can('manager') && (
                    <Th className="w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        className="h-4 w-4 rounded border-line bg-raised accent-[rgb(var(--brand))]"
                        checked={allSelected}
                        onChange={() =>
                          setSelected(allSelected ? new Set() : new Set(pendingIds))
                        }
                      />
                    </Th>
                  )}
                  <Th>Tenant</Th>
                  <Th>For</Th>
                  <Th align="right">Amount</Th>
                  <Th>Method</Th>
                  <Th>Paid on</Th>
                  <Th>Source</Th>
                  <Th>Status</Th>
                  <Th align="right">Action</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="row-hover">
                    {tab === 'pending' && can('manager') && (
                      <Td>
                        <input
                          type="checkbox"
                          aria-label={`Select payment from ${p.tenant_name}`}
                          className="h-4 w-4 rounded border-line bg-raised accent-[rgb(var(--brand))]"
                          checked={selected.has(p.id)}
                          onChange={() => toggle(p.id)}
                        />
                      </Td>
                    )}
                    <Td>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Avatar name={p.tenant_name ?? '?'} size="sm" />
                        <div className="min-w-0">
                          <Link to={`/app/tenants/${p.tenant_id}`} className="font-medium hover:text-brand truncate block">
                            {p.tenant_name}
                          </Link>
                          <p className="text-xs text-faint truncate">
                            {p.property_name}{p.room_name ? ` · ${p.room_name}` : ''}
                          </p>
                        </div>
                      </div>
                    </Td>
                    <Td className="max-w-[200px]">
                      <span className="text-muted truncate block">
                        {p.charge_description ?? <span className="text-faint italic">Unallocated</span>}
                      </span>
                    </Td>
                    <Td align="right" className="font-semibold tnum whitespace-nowrap">
                      {money(p.amount_cents, currency)}
                    </Td>
                    <Td className="text-muted whitespace-nowrap">{humanise(p.method)}</Td>
                    <Td className="text-muted whitespace-nowrap">{date(p.paid_at)}</Td>
                    <Td>
                      <Badge tone={p.source === 'tenant_portal' ? 'accent' : 'neutral'}>
                        {p.source === 'tenant_portal' ? 'Tenant' : 'Staff'}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge tone={statusTone(p.status)} dot>{humanise(p.status)}</Badge>
                    </Td>
                    <Td align="right">
                      <Button size="sm" variant="outline" onClick={() => setReviewing(p)}>
                        {p.status === 'pending' ? 'Review' : 'View'}
                      </Button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>
      )}

      {reviewing && (
        <ReviewModal
          payment={reviewing}
          onClose={() => setReviewing(null)}
          onDone={() => { setReviewing(null); reload(); }}
        />
      )}

      {recording && (
        <RecordPaymentModal
          onClose={() => setRecording(false)}
          onDone={() => { setRecording(false); reload(); }}
        />
      )}

      <ConfirmDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onConfirm={() => void bulkVerify()}
        loading={busy}
        tone="primary"
        title={`Verify ${selected.size} payment${selected.size === 1 ? '' : 's'}?`}
        confirmLabel="Verify all"
        message={
          <>
            These payments will count against the tenants' balances immediately.
            Check the proof on each one first — this is easy to do by accident and
            awkward to explain afterwards.
          </>
        }
      />
    </div>
  );
}

// ── Review a single claim ───────────────────────────────────────────────────

function ReviewModal({ payment, onClose, onDone }: {
  payment: Payment; onClose: () => void; onDone: () => void;
}) {
  const { currency, can } = useAuth();
  const toast = useToast();

  const [amountCents, setAmountCents] = useState(payment.amount_cents);
  const [chargeId, setChargeId] = useState(payment.charge_id ?? '');
  const [notify, setNotify] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  // Open charges this money could be applied to.
  const { data: charges } = useApi<Charge[]>('/charges', {
    tenant_id: payment.tenant_id, outstanding: true, limit: 50,
  });

  const proofFileId = (payment as any).proof_file_row_id as string | undefined;
  const isPending = payment.status === 'pending';
  const mismatch = amountCents !== payment.amount_cents;

  async function verify() {
    setBusy(true);
    try {
      await api.post(`/payments/${payment.id}/verify`, {
        amount_cents: amountCents,
        charge_id: chargeId || null,
        notify,
      });
      toast.success('Payment verified');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not verify that payment');
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    if (reason.trim().length < 3) {
      toast.error('Give a reason the tenant will understand');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/payments/${payment.id}/reject`, { reason: reason.trim(), notify });
      toast.success('Payment rejected');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not reject that payment');
    } finally {
      setBusy(false);
    }
  }

  async function refund() {
    if (reason.trim().length < 3) {
      toast.error('Give a reason for the reversal');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/payments/${payment.id}/refund`, { reason: reason.trim() });
      toast.success('Payment reversed');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not reverse that payment');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={isPending ? 'Review payment' : 'Payment details'}
      subtitle={`${payment.tenant_name} · ${money(payment.amount_cents, currency)} claimed`}
      footer={
        isPending && can('manager') ? (
          rejecting ? (
            <>
              <Button onClick={() => setRejecting(false)} disabled={busy}>Back</Button>
              <Button variant="danger" onClick={() => void reject()} loading={busy}
                icon={<XCircle className="h-4 w-4" />}>
                Reject payment
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setRejecting(true)} disabled={busy}>Reject…</Button>
              <Button variant="primary" onClick={() => void verify()} loading={busy}
                icon={<BadgeCheck className="h-4 w-4" />}>
                Verify {mismatch ? money(amountCents, currency) : 'payment'}
              </Button>
            </>
          )
        ) : payment.status === 'verified' && can('manager') ? (
          <>
            <Button onClick={onClose}>Close</Button>
            <Button variant="danger" onClick={() => void refund()} loading={busy}
              icon={<Undo2 className="h-4 w-4" />} disabled={reason.trim().length < 3}>
              Reverse payment
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      <div className="grid md:grid-cols-2 gap-6">
        {/* Proof of payment */}
        <div>
          <p className="label mb-2">Proof of payment</p>
          {proofFileId ? (
            <DriveImage
              fileId={proofFileId}
              alt="Payment proof"
              className="w-full rounded-xl border border-line object-contain max-h-80 bg-raised"
              fallback={<FileText className="h-8 w-8" />}
            />
          ) : (
            <div className="rounded-xl border border-dashed border-line p-8 text-center">
              <FileText className="h-7 w-7 text-faint mx-auto mb-2" />
              <p className="text-sm text-muted">No proof attached</p>
              <p className="text-xs text-faint mt-1">
                {payment.source === 'tenant_portal'
                  ? 'The tenant did not upload a receipt.'
                  : 'Recorded directly by staff.'}
              </p>
            </div>
          )}

          {payment.tenant_note && (
            <div className="mt-3 p-3 rounded-xl bg-raised border border-line">
              <p className="label mb-1">Tenant's note</p>
              <p className="text-sm text-muted italic">"{payment.tenant_note}"</p>
            </div>
          )}
        </div>

        {/* Claim details and decision */}
        <div className="space-y-4">
          <dl>
            <DetailRow label="Tenant">
              <Link to={`/app/tenants/${payment.tenant_id}`} className="link inline-flex items-center gap-1">
                {payment.tenant_name} <ExternalLink className="h-3 w-3" />
              </Link>
            </DetailRow>
            <DetailRow label="Property">
              {payment.property_name}{payment.room_name ? ` · ${payment.room_name}` : ''}
            </DetailRow>
            <DetailRow label="Method">{humanise(payment.method)}</DetailRow>
            <DetailRow label="Reference">
              <span className="font-mono text-xs">{payment.reference ?? '—'}</span>
            </DetailRow>
            <DetailRow label="Paid on">{date(payment.paid_at)}</DetailRow>
            <DetailRow label="Submitted">{relative(payment.created_at)}</DetailRow>
            {payment.verified_at && (
              <DetailRow label={payment.status === 'verified' ? 'Verified' : 'Reviewed'}>
                {dateTime(payment.verified_at)}
                {payment.verified_by_name ? ` · ${payment.verified_by_name}` : ''}
              </DetailRow>
            )}
            {payment.rejection_reason && (
              <DetailRow label="Reason">
                <span className="text-danger">{payment.rejection_reason}</span>
              </DetailRow>
            )}
          </dl>

          {isPending && can('manager') && !rejecting && (
            <div className="space-y-4 pt-2">
              <Field
                label="Amount to credit"
                hint={mismatch
                  ? `Tenant claimed ${money(payment.amount_cents, currency)} — you are crediting a different figure`
                  : 'Correct this if the proof shows a different amount'}
              >
                <MoneyInput valueCents={amountCents} onChangeCents={setAmountCents} currency={currency} />
              </Field>

              <Field
                label="Apply to"
                hint="Leave unallocated to hold it as credit on the tenant's account"
              >
                <Select value={chargeId} onChange={(e) => setChargeId(e.target.value)}>
                  <option value="">Unallocated credit</option>
                  {(charges ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.description} — {money(c.balance_cents ?? 0, currency)} outstanding
                    </option>
                  ))}
                </Select>
              </Field>

              <Checkbox
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
                label="Text the tenant a receipt"
                description="Sends now, using your 'Payment received' template"
              />
            </div>
          )}

          {isPending && rejecting && (
            <Field
              label="Why are you rejecting this?"
              hint="The tenant sees this in their portal"
              required
            >
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="No matching transfer found for this reference"
                autoFocus
              />
              <div className="mt-3">
                <Checkbox
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                  label="Text the tenant about this"
                />
              </div>
            </Field>
          )}

          {payment.status === 'verified' && can('manager') && (
            <Field
              label="Reverse this payment"
              hint="Use when a cheque bounces or a transfer is recalled"
            >
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason for reversal"
              />
            </Field>
          )}

          {!can('manager') && isPending && (
            <p className="text-sm text-warn bg-warn/10 border border-warn/25 rounded-xl px-3 py-2.5">
              Only a manager or owner can verify payments.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ── Record a payment by hand ────────────────────────────────────────────────

function RecordPaymentModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { currency, can } = useAuth();
  const toast = useToast();

  const [tenantId, setTenantId] = useState('');
  const [chargeId, setChargeId] = useState('');
  const [amountCents, setAmountCents] = useState(0);
  const [method, setMethod] = useState<string>('bank_transfer');
  const [reference, setReference] = useState('');
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [autoVerify, setAutoVerify] = useState(true);
  const [busy, setBusy] = useState(false);

  const { data: tenants } = useApi<Tenant[]>('/tenants', { status: 'active' });
  const { data: charges } = useApi<Charge[]>(
    tenantId ? '/charges' : null,
    { tenant_id: tenantId, outstanding: true, limit: 50 },
  );

  // Pre-fill the amount from the chosen charge — the common case is paying it in full.
  function pickCharge(id: string) {
    setChargeId(id);
    const charge = (charges ?? []).find((c) => c.id === id);
    if (charge?.balance_cents) setAmountCents(charge.balance_cents);
  }

  async function submit() {
    if (!tenantId) { toast.error('Choose a tenant'); return; }
    if (amountCents <= 0) { toast.error('Enter an amount'); return; }

    setBusy(true);
    try {
      await api.post('/payments', {
        tenant_id: tenantId,
        charge_id: chargeId || null,
        amount_cents: amountCents,
        method,
        reference: reference || null,
        paid_at: paidAt,
        tenant_note: note || null,
        auto_verify: autoVerify,
      });
      toast.success(autoVerify && can('manager') ? 'Payment recorded and verified' : 'Payment recorded for review');
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not record that payment');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Record a payment"
      subtitle="For rent handed over in person, or a transfer you have already seen"
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void submit()} loading={busy}>Record payment</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Tenant" required>
          <Select
            value={tenantId}
            onChange={(e) => { setTenantId(e.target.value); setChargeId(''); }}
          >
            <option value="">Choose a tenant…</option>
            {(tenants ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.full_name}{t.room_name ? ` — ${t.room_name}` : ''}
                {t.outstanding_cents ? ` (${money(t.outstanding_cents, currency)} owing)` : ''}
              </option>
            ))}
          </Select>
        </Field>

        {tenantId && (
          <Field label="Apply to" hint="Leave unallocated to hold it as credit">
            <Select value={chargeId} onChange={(e) => pickCharge(e.target.value)}>
              <option value="">Unallocated credit</option>
              {(charges ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.description} — {money(c.balance_cents ?? 0, currency)} outstanding
                </option>
              ))}
            </Select>
          </Field>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Amount" required>
            <MoneyInput valueCents={amountCents} onChangeCents={setAmountCents} currency={currency} />
          </Field>
          <Field label="Date paid" required>
            <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Method">
            <Select value={method} onChange={(e) => setMethod(e.target.value)}>
              {METHODS.map((m) => <option key={m} value={m}>{humanise(m)}</option>)}
            </Select>
          </Field>
          <Field label="Reference" hint="Transaction ID or receipt number">
            <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="MPESA-QF7723L" />
          </Field>
        </div>

        <Field label="Note">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
        </Field>

        {can('manager') ? (
          <Checkbox
            checked={autoVerify}
            onChange={(e) => setAutoVerify(e.target.checked)}
            label="Verify immediately"
            description="Untick to send it to the review queue instead"
          />
        ) : (
          <p className="text-sm text-muted bg-raised border border-line rounded-xl px-3 py-2.5">
            This will go to the review queue — a manager verifies payments.
          </p>
        )}
      </div>
    </Modal>
  );
}
